import {
	Component, Input, OnInit, OnChanges, SimpleChanges, SimpleChange,
	ChangeDetectionStrategy, ChangeDetectorRef
} from '@angular/core';
import {
	InfoSetView, InfoSetViewColumn, InfoSetViewEntry, InfoSetViewValue
} from 'UvExplorerServer.Dtos.TS/Session';
import { DateService } from 'base';
import { StringUtils } from 'DiscoveryEngine.TS/Utils';
import { IdentityService } from 'identity';
import { DiscResultsService } from 'discovery';

@Component({
    selector: 'config-info-viewer',
	templateUrl: './configinfoviewer.component.html',
	styleUrls: ['./configinfoviewer.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ConfigInfoViewerComponent implements OnInit, OnChanges {

	@Input()
	public deviceGuid: string;

	@Input()
	public deviceName: string;

	@Input()
	public infoSetView: InfoSetView;

	public entries: any[];
	public configKey: string;
	public configInitialCapture: string;
	public configLastCapture: string;
	public configText: string;
	public displayViewConfigDialog: boolean;
	public displayDiffConfigDialog: boolean;

	public displayDeleteResultConfirm: boolean = false;
	public deleteConfigKey: string;
	public deleteLastCaptureTime: string;

	constructor(private changeDetector: ChangeDetectorRef,
		private dateService: DateService,
		private identityService: IdentityService,
		private discResultsService: DiscResultsService ) {

		this.displayViewConfigDialog = false;
		this.displayDiffConfigDialog = false;

		this.resetConfigProps();

		// Force infoset viewer to update its contents (bug work-around)
		setTimeout(() => {
			this.updateData();
			this.changeDetector.markForCheck();
		}, 0);
	}

	public formatDateHm(date: string | Date): string {
		return this.dateService.formatDateHm(date);
	}

	private resetConfigProps(): void {
		this.configKey = "";
		this.configInitialCapture = "";
		this.configLastCapture = "";
		this.configText = "";
	}

	private readonly ObfuscateConfigs = false;

	private setConfigPropsFromEntry(entry): void {
		this.configKey = entry.configKey;
		this.configInitialCapture = entry.initialCapture;
		this.configLastCapture = entry.lastCapture;
		this.configText = (this.ObfuscateConfigs ? StringUtils.ClarifyString2(entry.configText) : entry.configText);
	}

	public viewConfig(event, entry): void {
		event.stopPropagation();
		this.setConfigPropsFromEntry(entry);
		this.displayViewConfigDialog = true;
	}

	public closeViewConfigDialog(): void {
		this.displayViewConfigDialog = false;
		this.resetConfigProps();
	}

	public diffConfig(event, entry): void {
		event.stopPropagation();
		this.setConfigPropsFromEntry(entry);
		this.displayDiffConfigDialog = true;
	}

	public closeDiffConfigDialog(): void {
		this.displayDiffConfigDialog = false;
		this.resetConfigProps();
	}

	public ngOnInit(): void {
		this.updateData();
	}

	public ngOnChanges(changes: SimpleChanges): void {
		let change = changes["infoSetView"];

		if (change) {
			this.updateData();
		}
	}

	private updateData(): void {
		this.entries = [];

		if (this.infoSetView) {

			// Initialize entries
			this.infoSetView.entries.forEach((viewEntry: InfoSetViewEntry) => {
				let entry: any = {};

				entry.configKey = viewEntry.values[0].value;
				entry.initialCapture = viewEntry.values[1].value;
				entry.lastCapture = viewEntry.values[2].value;
				entry.configText = viewEntry.values[3].value;

				this.entries.push(entry);
			}, this);
		}
	}

	private removeEntry(configKey: string, lastCapture: string): void {

		if (this.infoSetView) {

			if (configKey === "") {
				while (this.infoSetView.entries.length > 0) {
					this.infoSetView.entries.pop();
				}
			}
			else if (lastCapture === "") {
				let index = 0;
				let indexes: Array<number> = [];

				// Initialize entries
				this.infoSetView.entries.forEach((viewEntry: InfoSetViewEntry) => {
					let entry: any = {};

					entry.configKey = viewEntry.values[0].value;
					entry.initialCapture = viewEntry.values[1].value;
					entry.lastCapture = viewEntry.values[2].value;
					entry.configText = viewEntry.values[3].value;

					if ((entry.configKey == configKey)) {
						indexes.push(index);
					}
					index++;
				}, this);


				if (indexes.length > 0) {
					indexes.reverse();

					for (const entryIndex of indexes) {
						this.infoSetView.entries.splice(entryIndex, 1);
					}
				}
			}
			else {
				let foundIndex = -1;
				let index = 0;

				// Initialize entries
				this.infoSetView.entries.forEach((viewEntry: InfoSetViewEntry) => {
					let entry: any = {};

					entry.configKey = viewEntry.values[0].value;
					entry.initialCapture = viewEntry.values[1].value;
					entry.lastCapture = viewEntry.values[2].value;
					entry.configText = viewEntry.values[3].value;

					if ((entry.configKey == configKey) && (entry.lastCapture == lastCapture)) {
						foundIndex = index;
					}
					index++;

				}, this);

				if (foundIndex >= 0) {
					this.infoSetView.entries.splice(foundIndex, 1);
				}
			}
		}
	}

	public get isAdminValid(): boolean {
		return this.identityService.isAdmin;
	}

	public doCustomSort(event): void {
		let _self = this;
		let comparer = function (a: any, b: any): number {

			let result = 0;
			if (event.field == "configKey") {
				let aVal: string = a[event.field];
				let bVal: string = b[event.field];
				result = _self.compareStrings(aVal, bVal);
			}
			else if (event.field == "initialCapture" || event.field == "lastCapture") {
				let aVal: Date = a[event.field];
				let bVal: Date = b[event.field];
				result = _self.compareDates(aVal, bVal);
			}
			else if (event.field == "config") {
				throw new Error("Cannot sort on config field");
			}
			else {
				throw new Error("Invalid sort field: " + event.field);
			}

			return result * event.order;
		}

		this.entries.sort(comparer);
	}

	protected compareStrings(a: string, b: string): number {
		if (a && b)
			return a.localeCompare(b);
		else if (a)
			return 1;
		else if (b)
			return -1;
		else
			return 0;
	}

	protected compareDates(a: Date, b: Date): number {
		if (a !== undefined && b !== undefined)
			return ((a < b) ? -1 : ((a > b) ? 1 : 0));
		else if (a !== undefined)
			return 1;
		else if (b !== undefined)
			return -1;
		else
			return 0;
	}

	public exportConfig(): void {
		//let data = [this.config];
		//let blob = new Blob(data, { type: "application/vnd.ms-powerpoint" });
		//let blobUrl = window.URL.createObjectURL(blob);
		//window.open(blobUrl);
		this.download(this.configText, this.configKey + ".txt", "text/plain");
	}

	// Function to download data to a file
	private download(data, filename, type): void {
		var file = new Blob([data], { type: type });
	
		var a = document.createElement("a"),
			url = URL.createObjectURL(file);
		a.href = url;
		a.download = filename;
		document.body.appendChild(a);
		a.click();
		setTimeout(function () {
			document.body.removeChild(a);
			window.URL.revokeObjectURL(url);
		}, 0);
		
	}

	public tryDeleteResult(event: any, entry): void {
		event.stopPropagation();
		this.deleteConfigKey = entry.configKey;
		this.deleteLastCaptureTime = entry.lastCapture;
		this.displayDeleteResultConfirm = true;
	}

	public tryDeleteKeyResults(event: any, entry): void {
		event.stopPropagation();
		this.deleteConfigKey = entry.configKey;
		this.deleteLastCaptureTime = "";
		this.displayDeleteResultConfirm = true;
	}

	public tryDeleteAllResult(event: any, entry): void {
		event.stopPropagation();
		this.deleteConfigKey = "";
		this.deleteLastCaptureTime = "";
		this.displayDeleteResultConfirm = true;
	}

	public deleteResult(): void {

		this.discResultsService.deleteMasterDeviceConfigEntry(this.deviceGuid, this.deleteConfigKey, this.deleteLastCaptureTime).subscribe({
			next: (result: boolean) => {

				this.removeEntry(this.deleteConfigKey, this.deleteLastCaptureTime);

				this.deleteConfigKey = "";
				this.deleteLastCaptureTime = "";
				this.displayDeleteResultConfirm = false;

				this.updateData();
			}
		});

		this.displayDeleteResultConfirm = false;
	}

	public cancelDeleteResult(): void {
		this.deleteConfigKey = "";
		this.deleteLastCaptureTime = "";
		this.displayDeleteResultConfirm = false;
	}

}
